import { DisplayCue, ProductInfo } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { getRootDispatcher } from "@/analytics";
import { getDisplayName, type HoneycombEligibleProducts, sendToHoneycomb } from "@/analytics/sendToHoneycomb";
import { getSelectedSessionId } from "@/sdk";
import { type EligibleProduct, useProductOfferStore } from "../stores";
import { type EligibilityRequest, EligibilityStatus } from "../types";
import { getSalesApiClient } from "./api";

const logger = getLogger({
    module: "eligibility",
    supportTeam: "sales",
});

export type MdnLookupResult = {
    hasReachedMaxMdnLookups: boolean;
    previousHashedMdnLookups: string[];
};

export type EligibilityMeta = {
    mdnLookupResult: MdnLookupResult;
};

export type CheckEligibilityResult = {
    productConfigs: EligibleProduct[];
    meta: EligibilityMeta;
    displayCues?: DisplayCue[];
};

export const getEligibility = async (eligibility: EligibilityRequest): Promise<CheckEligibilityResult> => {
    const childLogger = logger.child({ sessionId: getSelectedSessionId() });
    try {
        const salesApi = getSalesApiClient();

        const salesApiRes = await salesApi
            .post("v2/eligibility", {
                json: eligibility,
                retry: 0, // retries already done via @tanstack/react-query
            })
            .json<CheckEligibilityResult>();

        return handleResponse(eligibility, salesApiRes);
    } catch (err: unknown) {
        childLogger.error({ err }, `eligibility API call failed`);
        throw err;
    }
};

const handleResponse = (
    eligibility: EligibilityRequest,
    checkEligibilityResult: CheckEligibilityResult,
): CheckEligibilityResult => {
    checkEligibilityResult.productConfigs.forEach((result) => {
        let dispatcher = getRootDispatcher().withExtra({
            productSku: result.productSku,
            productOfferId: result.id,
            eligibilityStatus: result.status,
        });

        if (eligibility.reservationSid) {
            dispatcher = dispatcher.withIdentity("ReservationSid", eligibility.reservationSid);
        }

        void dispatcher.dispatchBusinessEvent("CustomerValidated");
        void dispatcher.dispatchAction("ApiResult", "EligibilityStatus");
        trackFullStoryEvent("EligibilityStatus", {
            productSku: result.productSku,
            eligibilityStatus: result.status,
            productOfferId: result.id,
            reservationSid: eligibility.reservationSid ?? "unknown",
            sessionId: eligibility.sessionId,
            partner: eligibility.partner,
            hasAddress: !!eligibility.address,
        });
    });

    useProductOfferStore
        .getState()
        .updateProductInfo(eligibility.sessionId, mapToProductInfoMap(checkEligibilityResult.productConfigs));
    useProductOfferStore.getState().updateProducts(eligibility.sessionId, checkEligibilityResult.productConfigs);
    const honeycombEligibility: HoneycombEligibleProducts[] = checkEligibilityResult.productConfigs
        .filter((p) => p.status === EligibilityStatus.Eligible)
        .map((p) => {
            return {
                productSku: p.productSku,
                productOfferId: p.id,
                eligibilityStatus: p.status,
                displayName: getDisplayName(p.productInfo),
            };
        });
    sendToHoneycomb(eligibility.sessionId, { eligibility: honeycombEligibility });

    return checkEligibilityResult;
};

function mapToProductInfoMap(productInfo: EligibleProduct[]): Record<string, ProductInfo> | undefined {
    if (!productInfo.length) {
        return {};
    }

    const pMap: Record<string, ProductInfo> = {};
    productInfo.forEach((p) => {
        if (p.productInfo) {
            if ("en-US" in p.productInfo) {
                pMap[p.productSku] = (p.productInfo as Record<string, ProductInfo>)["en-US"];
            } else {
                pMap[p.productSku] = p.productInfo as ProductInfo;
            }
        }
    });
    return pMap;
}
